import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Waves, Moon, Sparkles, Sun, Cloud, Flame, Wind, Droplets, Thermometer, ExternalLink, Star, ArrowLeft } from "lucide-react";
import EditableText from "@/components/editor/EditableText";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 5 * 60 * 1000;

/* ─── Shared Components (reused from TonightDashboard) ─── */
function Panel({ icon: Icon, title, id, children, delay = 0, className = "" }) {
  return (
    <motion.div id={id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className={`bg-[#1E2A1A] border border-[#737570]/20 rounded-md p-5 sm:p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-[#7A9E6E]" />
        <span className="font-body text-[#737570] text-xs tracking-widest uppercase">{title}</span>
      </div>
      {children}
    </motion.div>
  );
}

function MoonVisual({ fraction, size = 90 }) {
  const r = size / 2 - 6, cx = size / 2, cy = size / 2;
  const sweep = fraction <= 0.5 ? fraction * 2 : (1 - fraction) * 2;
  const isWaxing = fraction <= 0.5;
  let pathD = "";
  if (sweep > 0.01 && sweep < 0.99) {
    const rx = Math.max(0.5, Math.abs(r * (1 - 2 * sweep)));
    const sf = sweep > 0.5 ? 1 : 0;
    pathD = isWaxing
      ? `M ${cx} ${cy-r} A ${r} ${r} 0 0 1 ${cx} ${cy+r} A ${rx} ${r} 0 0 ${sf} ${cx} ${cy-r}`
      : `M ${cx} ${cy-r} A ${r} ${r} 0 0 0 ${cx} ${cy+r} A ${rx} ${r} 0 0 ${1-sf} ${cx} ${cy-r}`;
  } else if (sweep >= 0.99) {
    pathD = `M ${cx} ${cy-r} A ${r} ${r} 0 0 1 ${cx} ${cy+r} A ${r} ${r} 0 0 1 ${cx} ${cy-r}`;
  }
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <circle cx={cx} cy={cy} r={r} fill="#1E2A1A" stroke="#737570" strokeWidth="0.5" />
      {pathD && <path d={pathD} fill="#E4E4E0" opacity="0.92" />}
    </svg>
  );
}

function TideDial({ tideData }) {
  if (!tideData) return null;
  const phase = tideData.phase_percent || 0;
  const SIZE = 360, CX = 180, CY = 180;
  // Concentric radii
  const R_OUTER = 170;   // bezel outer
  const R_BEZEL = 158;   // bezel inner / dial edge
  const R_TICKS = 148;   // tick ring
  const R_LABELS = 130;  // hour labels
  const R_WATER = 118;   // inner water disc
  const R_HAND = 100;    // hand tip

  // Hand angle: 0% = 12 o'clock (HIGH), 50% = 6 o'clock (LOW), rotates full 360 over a tide cycle
  const angle = (phase / 100) * 360;
  const rad = ((angle - 90) * Math.PI) / 180;
  const hx = CX + R_HAND * Math.cos(rad);
  const hy = CY + R_HAND * Math.sin(rad);
  // Counterweight (short tail on opposite side of hand)
  const tailRad = ((angle + 90) * Math.PI) / 180;
  const tx = CX + 22 * Math.cos(tailRad);
  const ty = CY + 22 * Math.sin(tailRad);

  // Water fill level — sinusoidal so it pulses between LOW and HIGH
  const fillPct = (Math.cos((phase * Math.PI) / 50) + 1) / 2; // 0 (low) → 1 (high)
  const wY = CY + R_WATER - fillPct * 2 * R_WATER; // y where waterline sits

  // Wavy crest path across the water disc at wY
  const waveAmp = 3;
  const waveFreq = 4;
  const wavePts = [];
  const steps = 40;
  for (let i = 0; i <= steps; i++) {
    const x = CX - R_WATER + (i / steps) * 2 * R_WATER;
    const y = wY + Math.sin((i / steps) * Math.PI * waveFreq) * waveAmp;
    wavePts.push(`${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  const wavePath = [...wavePts, `L ${CX + R_WATER} ${CY + R_WATER}`, `L ${CX - R_WATER} ${CY + R_WATER}`, "Z"].join(" ");

  // Hour tick marks (24 positions; 6 cardinals at every 6 ticks = 4 positions)
  const ticks = Array.from({ length: 60 }, (_, i) => {
    const a = ((i * 6 - 90) * Math.PI) / 180;
    const isHour = i % 5 === 0;
    const isCardinal = i % 15 === 0;
    const inner = isCardinal ? R_TICKS - 14 : isHour ? R_TICKS - 9 : R_TICKS - 4;
    const outer = R_TICKS;
    return {
      x1: CX + inner * Math.cos(a),
      y1: CY + inner * Math.sin(a),
      x2: CX + outer * Math.cos(a),
      y2: CY + outer * Math.sin(a),
      w: isCardinal ? 1.8 : isHour ? 1.1 : 0.6,
      c: isCardinal ? "#c9a96e" : isHour ? "#B0B1AD" : "#737570",
    };
  });

  // Hour numerals — regular Arabic
  const cardinals = [
    { x: CX, y: CY - R_LABELS + 6, label: "12" },
    { x: CX + R_LABELS - 6, y: CY + 5, label: "3" },
    { x: CX, y: CY + R_LABELS - 2, label: "6" },
    { x: CX - R_LABELS + 6, y: CY + 5, label: "9" },
  ];

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full max-w-[340px]" role="img" aria-label="Tide clock">
      <defs>
        {/* Brass bezel gradient */}
        <linearGradient id="td-bezel" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#d9bf87" />
          <stop offset="30%" stopColor="#9a8456" />
          <stop offset="55%" stopColor="#c9a96e" />
          <stop offset="100%" stopColor="#6d5d3a" />
        </linearGradient>
        {/* Dial face */}
        <radialGradient id="td-face" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#263222" />
          <stop offset="70%" stopColor="#1a2418" />
          <stop offset="100%" stopColor="#0e140c" />
        </radialGradient>
        {/* Water gradient */}
        <linearGradient id="td-water" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7A9E6E" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#2d4a26" stopOpacity="0.85" />
        </linearGradient>
        {/* Hand gradient */}
        <linearGradient id="td-hand" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#e0c490" />
          <stop offset="100%" stopColor="#c9a96e" />
        </linearGradient>
        {/* Clip to the inner water disc */}
        <clipPath id="td-water-clip">
          <circle cx={CX} cy={CY} r={R_WATER} />
        </clipPath>
        {/* Curved path for top arc label (hugs the tick ring, above the numerals) */}
        <path id="td-arc-top" d={`M ${CX - 60} ${CY - 139} A 140 140 0 0 1 ${CX + 60} ${CY - 139}`} fill="none" />
      </defs>

      {/* Outer drop shadow ring */}
      <circle cx={CX} cy={CY} r={R_OUTER + 4} fill="none" stroke="#000" strokeOpacity="0.4" strokeWidth="2" />

      {/* Brass bezel */}
      <circle cx={CX} cy={CY} r={R_OUTER} fill="url(#td-bezel)" />
      {/* Bezel inner ring (darker groove) */}
      <circle cx={CX} cy={CY} r={R_BEZEL + 3} fill="none" stroke="#3a3020" strokeWidth="1.5" />
      <circle cx={CX} cy={CY} r={R_BEZEL} fill="url(#td-face)" />

      {/* Bezel decorative notches (every 15°, outside the face) */}
      {Array.from({ length: 24 }).map((_, i) => {
        const a = ((i * 15 - 90) * Math.PI) / 180;
        const r1 = R_OUTER - 2;
        const r2 = R_BEZEL + 1;
        return <line key={`notch-${i}`} x1={CX + r1 * Math.cos(a)} y1={CY + r1 * Math.sin(a)} x2={CX + r2 * Math.cos(a)} y2={CY + r2 * Math.sin(a)} stroke="#3a3020" strokeOpacity="0.55" strokeWidth="0.8" />;
      })}

      {/* Inner tick ring background */}
      <circle cx={CX} cy={CY} r={R_TICKS + 2} fill="none" stroke="#c9a96e" strokeOpacity="0.18" strokeWidth="0.6" />
      <circle cx={CX} cy={CY} r={R_TICKS - 18} fill="none" stroke="#c9a96e" strokeOpacity="0.12" strokeWidth="0.5" />

      {/* Tick marks */}
      {ticks.map((t, i) => <line key={`tick-${i}`} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={t.c} strokeWidth={t.w} strokeLinecap="round" />)}

      {/* Hour numerals */}
      {cardinals.map((c, i) => (
        <text key={`card-${i}`} x={c.x} y={c.y} textAnchor="middle" fill="#c9a96e" fontSize="15" fontFamily="Playfair Display" fontWeight="700">{c.label}</text>
      ))}

      {/* Arc label — "Bay of Fundy" across the top */}
      <text fill="#c9a96e" fontSize="7" fontFamily="DM Sans" fontWeight="500" letterSpacing="4" opacity="0.65">
        <textPath href="#td-arc-top" startOffset="50%" textAnchor="middle">BAY · OF · FUNDY</textPath>
      </text>

      {/* Inner water disc */}
      <circle cx={CX} cy={CY} r={R_WATER} fill="#0a0f08" />
      {/* Water fill (clipped to disc) */}
      <g clipPath="url(#td-water-clip)">
        <path d={wavePath} fill="url(#td-water)" />
        {/* Crest highlight line */}
        <path d={wavePts.join(" ")} fill="none" stroke="#a5c396" strokeOpacity="0.55" strokeWidth="1" />
      </g>
      {/* Water disc rim */}
      <circle cx={CX} cy={CY} r={R_WATER} fill="none" stroke="#c9a96e" strokeOpacity="0.25" strokeWidth="0.8" />

      {/* (Height readout is shown below the dial) */}

      {/* Hand counterweight (tail) */}
      <line x1={CX} y1={CY} x2={tx} y2={ty} stroke="#c9a96e" strokeOpacity="0.6" strokeWidth="2" strokeLinecap="round" />

      {/* Main hand — tapered arrow */}
      <g transform={`rotate(${angle} ${CX} ${CY})`}>
        <polygon
          points={`${CX - 3},${CY + 10} ${CX + 3},${CY + 10} ${CX + 1.2},${CY - R_HAND + 10} ${CX},${CY - R_HAND} ${CX - 1.2},${CY - R_HAND + 10}`}
          fill="url(#td-hand)"
          stroke="#8a7442"
          strokeWidth="0.4"
        />
        {/* Decorative hand diamond midway */}
        <circle cx={CX} cy={CY - R_HAND * 0.55} r="3" fill="#e0c490" stroke="#8a7442" strokeWidth="0.4" />
      </g>

      {/* Center cap — jewel */}
      <circle cx={CX} cy={CY} r="8" fill="#2C3828" stroke="#c9a96e" strokeWidth="1" />
      <circle cx={CX} cy={CY} r="5" fill="url(#td-bezel)" />
      <circle cx={CX - 1.5} cy={CY - 1.5} r="1.5" fill="#fff" fillOpacity="0.35" />
    </svg>
  );
}

function KPGauge({ kp }) {
  const angle=Math.min(kp/9,1)*180, r=50, cx=60, cy=58;
  const sA=Math.PI, eA=Math.PI-(angle*Math.PI)/180;
  const x1=cx+r*Math.cos(sA),y1=cy+r*Math.sin(sA),x2=cx+r*Math.cos(eA),y2=cy+r*Math.sin(eA);
  return (
    <svg viewBox="0 0 120 70" className="w-full max-w-[160px]">
      <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`} fill="none" stroke="#1E2A1A" strokeWidth="6" strokeLinecap="round"/>
      {angle>0&&<path d={`M ${x1} ${y1} A ${r} ${r} 0 ${angle>180?1:0} 0 ${x2} ${y2}`} fill="none" stroke={kp>=7?"#c0392b":kp>=5?"#e67e22":kp>=3?"#f1c40f":"#7A9E6E"} strokeWidth="6" strokeLinecap="round"/>}
      <text x={cx} y={cy-8} textAnchor="middle" fill="#E4E4E0" fontSize="22" fontFamily="Playfair Display" fontWeight="700">{kp}</text>
      <text x={cx} y={cy+4} textAnchor="middle" fill="#737570" fontSize="6" fontFamily="DM Sans" letterSpacing="1">KP INDEX</text>
    </svg>
  );
}

function TimelineBar({ sun }) {
  if (!sun?.hours) return null;
  const h=sun.hours, W=100, H=28, toX=hr=>hr!=null?(hr/24)*W:null;
  const now=new Date(), nowHr=now.getHours()+now.getMinutes()/60, nowX=toX(nowHr);
  const zones=[
    {start:0,end:h.astronomical_twilight_begin,color:"#0a0f08"},{start:h.astronomical_twilight_begin,end:h.nautical_twilight_begin,color:"#151d13"},
    {start:h.nautical_twilight_begin,end:h.civil_twilight_begin,color:"#1E2A1A"},{start:h.civil_twilight_begin,end:h.sunrise,color:"#2C3828"},
    {start:h.sunrise,end:h.sunset,color:"#3d4d38"},{start:h.sunset,end:h.civil_twilight_end,color:"#2C3828"},
    {start:h.civil_twilight_end,end:h.nautical_twilight_end,color:"#1E2A1A"},{start:h.nautical_twilight_end,end:h.astronomical_twilight_end,color:"#151d13"},
    {start:h.astronomical_twilight_end,end:24,color:"#0a0f08"},
  ].filter(z=>z.start!=null&&z.end!=null);
  return (
    <svg viewBox={`0 0 ${W} ${H+14}`} className="w-full" preserveAspectRatio="none">
      <rect x="0" y="8" width={W} height={H} rx="2" fill="#0a0f08"/>
      {zones.map((z,i)=><rect key={i} x={toX(z.start)} y="8" width={toX(z.end)-toX(z.start)} height={H} fill={z.color}/>)}
      {sun.golden_hour_start_hour&&<rect x={toX(sun.golden_hour_start_hour)} y="8" width={toX(h.sunset)-toX(sun.golden_hour_start_hour)} height={H} fill="#7A9E6E" opacity="0.25"/>}
      {nowX&&<><line x1={nowX} y1="6" x2={nowX} y2={H+10} stroke="#7A9E6E" strokeWidth="0.6"/><circle cx={nowX} cy="6" r="1.5" fill="#7A9E6E"/></>}
      {h.sunrise&&<text x={toX(h.sunrise)} y="6" textAnchor="middle" fill="#B0B1AD" fontSize="2.8" fontFamily="DM Sans">{sun.sunrise}</text>}
      {h.sunset&&<text x={toX(h.sunset)} y="6" textAnchor="middle" fill="#B0B1AD" fontSize="2.8" fontFamily="DM Sans">{sun.sunset}</text>}
    </svg>
  );
}

/* ─── Star Chart — Planisphere ─── */
function StarChart({ stars }) {
  if (!stars?.chart_stars) return null;
  const R = stars.radius || 100;
  const S = R * 2;
  const magToSize = (m) => Math.max(0.5, 3.5 - m * 0.7);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <p className="font-display text-[#E4E4E0] text-lg">Tonight's Sky</p>
          <p className="font-body text-[#737570] text-xs">{stars.bortle_note}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${stars.milky_way_visible ? "bg-[#7A9E6E]" : "bg-[#737570]"}`} />
          <span className="font-body text-[#737570] text-xs">{stars.milky_way_visible ? "Milky Way core visible" : "Milky Way core below horizon"}</span>
        </div>
      </div>

      {/* Planisphere SVG */}
      <div className="flex justify-center">
        <svg viewBox={`-12 -12 ${S + 24} ${S + 24}`} className="w-full max-w-[360px]" style={{ filter: "drop-shadow(0 0 20px rgba(122,158,110,0.08))" }}>
          <defs>
            <radialGradient id="sky-grad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#0a100d" />
              <stop offset="70%" stopColor="#0d1510" />
              <stop offset="100%" stopColor="#151d18" />
            </radialGradient>
            <clipPath id="sky-clip"><circle cx={R} cy={R} r={R} /></clipPath>
          </defs>

          {/* Sky dome */}
          <circle cx={R} cy={R} r={R + 1} fill="none" stroke="#737570" strokeWidth="0.5" opacity="0.3" />
          <circle cx={R} cy={R} r={R} fill="url(#sky-grad)" />

          {/* Altitude rings */}
          {[30, 60].map(alt => (
            <circle key={alt} cx={R} cy={R} r={(90 - alt) / 90 * R} fill="none" stroke="#737570" strokeWidth="0.2" opacity="0.15" strokeDasharray="2 3" />
          ))}

          {/* Constellation lines */}
          <g clipPath="url(#sky-clip)">
            {stars.chart_lines.map((l, i) => (
              <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#7A9E6E" strokeWidth="0.5" opacity="0.35" />
            ))}
          </g>

          {/* Stars */}
          <g clipPath="url(#sky-clip)">
            {stars.chart_stars.filter(s => s.alt > 0).map((s, i) => (
              <circle key={i} cx={s.x} cy={s.y} r={magToSize(s.mag)}
                fill={s.mag < 0.5 ? "#F0EFEB" : s.mag < 1.5 ? "#E4E4E0" : s.mag < 2.5 ? "#B0B1AD" : "#737570"}
                opacity={s.mag < 1 ? 0.95 : s.mag < 2 ? 0.8 : 0.6} />
            ))}
          </g>

          {/* Constellation labels */}
          <g clipPath="url(#sky-clip)">
            {stars.chart_labels.map((l, i) => (
              <text key={i} x={l.x} y={l.y - 5} textAnchor="middle" fill="#7A9E6E" fontSize="4" fontFamily="DM Sans" opacity="0.6" letterSpacing="0.5">
                {l.name}
              </text>
            ))}
          </g>

          {/* Bright star labels */}
          <g clipPath="url(#sky-clip)">
            {stars.chart_stars.filter(s => s.mag < 0.5 && s.alt > 5).map((s, i) => (
              <text key={i} x={s.x + 4} y={s.y + 1.5} fill="#E4E4E0" fontSize="3" fontFamily="DM Sans" opacity="0.5">
                {s.name}
              </text>
            ))}
          </g>

          {/* Cardinal directions */}
          <text x={R} y={-3} textAnchor="middle" fill="#B0B1AD" fontSize="5" fontFamily="DM Sans" fontWeight="600">N</text>
          <text x={R} y={S + 7} textAnchor="middle" fill="#737570" fontSize="5" fontFamily="DM Sans" fontWeight="600">S</text>
          <text x={-5} y={R + 2} textAnchor="middle" fill="#737570" fontSize="5" fontFamily="DM Sans" fontWeight="600">E</text>
          <text x={S + 5} y={R + 2} textAnchor="middle" fill="#737570" fontSize="5" fontFamily="DM Sans" fontWeight="600">W</text>

          {/* Zenith marker */}
          <circle cx={R} cy={R} r="1" fill="none" stroke="#737570" strokeWidth="0.3" opacity="0.3" />
        </svg>
      </div>

      <p className="font-body text-[#737570]/50 text-xs text-center">{stars.milky_way_note}</p>
    </div>
  );
}



function fmt(min) { if (!min&&min!==0) return "--"; const h=Math.floor(min/60),m=min%60; return h>0?`${h}h ${m}m`:`${m}m`; }
const alertColors={Quiet:"bg-[#7A9E6E]/20 text-[#7A9E6E]",Watch:"bg-yellow-500/20 text-yellow-400",Active:"bg-orange-500/20 text-orange-400",Storm:"bg-red-500/20 text-red-400"};
const starColors={good:"text-[#7A9E6E]",fair:"text-yellow-400",poor:"text-[#737570]"};

/* ─── Main Page ─── */
export default function EnvironmentPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(new Date());

  const fetchData = useCallback(async () => {
    try { const res = await axios.get(`${API}/tonight`); setData(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { const i = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(i); }, []);

  return (
    <div className="min-h-screen bg-[#2C3828]" data-testid="environment-page">
      <header className="pt-24 pb-8 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}}
            className="font-display text-[#E4E4E0] text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
            The Environment
          </motion.h1>
          <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{duration:0.8,delay:0.2}} className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-display text-[#E4E4E0]/80 text-lg">{clock.toLocaleTimeString("en-CA",{hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:false,timeZone:"America/Halifax"})}</span>
            <span className="font-body text-[#737570] text-sm">{data?.date||""}</span>
            <span className="font-body text-[#737570]/60 text-xs">Digby Neck &middot; Nova Scotia</span>
          </motion.div>
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#7A9E6E] border-t-transparent rounded-full animate-spin"/></div>
      ) : (
        <div className="px-4 sm:px-8 pb-16">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tide */}
            <Panel icon={Waves} title="Tides" id="tides" delay={0.1} className="md:col-span-2">
              <div className="flex flex-col items-center">
                <TideDial tideData={data?.tide}/>

                {/* Fancy readout below the dial */}
                <div className="mt-6 flex items-center gap-5 sm:gap-8">
                  <span className="h-px w-10 sm:w-16 bg-[#c9a96e]/40" />
                  <div className="text-center">
                    <p className="font-body text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] mb-1">{data?.tide?.state}</p>
                    <p className="font-display text-[#E4E4E0] text-3xl sm:text-4xl font-semibold tracking-tight">
                      {data?.tide?.height_ft?.toFixed(1)}<span className="font-accent italic text-[#c9a96e] text-lg sm:text-xl ml-1">ft</span>
                    </p>
                  </div>
                  <span className="h-px w-10 sm:w-16 bg-[#c9a96e]/40" />
                </div>

                <div className="grid grid-cols-2 gap-12 mt-6 w-full max-w-[360px]">
                  <div className="text-center border-r border-[#737570]/20 pr-4">
                    <p className="font-body text-[#737570] text-[10px] uppercase tracking-widest mb-1">Next High</p>
                    <p className="font-display text-[#E4E4E0] text-lg">{fmt(data?.tide?.time_to_next_high_minutes)}</p>
                  </div>
                  <div className="text-center pl-4">
                    <p className="font-body text-[#737570] text-[10px] uppercase tracking-widest mb-1">Next Low</p>
                    <p className="font-display text-[#E4E4E0] text-lg">{fmt(data?.tide?.time_to_next_low_minutes)}</p>
                  </div>
                </div>
              </div>
            </Panel>

            {/* Star Chart */}
            <Panel icon={Star} title="Star Chart" id="stars" delay={0.15}>
              <StarChart stars={data?.stars}/>
            </Panel>

            {/* Moon & Sky */}
            <Panel icon={Moon} title="Moon & Sky" id="moon" delay={0.2}>
              <div className="flex items-start gap-5">
                <MoonVisual fraction={data?.moon?.phase_fraction||0}/>
                <div className="flex-1 space-y-2">
                  <p className="font-display text-[#E4E4E0] text-lg">{data?.moon?.phase_name}</p>
                  <p className="font-body text-[#737570] text-sm">{data?.moon?.illumination}% illuminated</p>
                  <div className="flex gap-4 text-xs font-body text-[#737570]">
                    {data?.moon?.moonrise&&<span>Rise {data.moon.moonrise}</span>}
                    {data?.moon?.moonset&&<span>Set {data.moon.moonset}</span>}
                  </div>
                  <div className="mt-2 pt-2 border-t border-[#737570]/15">
                    <span className="font-body text-xs text-[#737570]">Stargazing: </span>
                    <span className={`font-body text-xs font-semibold uppercase tracking-wide ${starColors[data?.moon?.stargazing_rating]||""}`}>{data?.moon?.stargazing_rating}</span>
                  </div>
                </div>
              </div>
            </Panel>

            {/* Aurora */}
            <Panel icon={Sparkles} title="Aurora Watch" id="aurora" delay={0.25}>
              <div className="flex flex-col items-center">
                <KPGauge kp={data?.aurora?.kp_index||0}/>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-body font-medium mt-2 ${alertColors[data?.aurora?.alert_state]||alertColors.Quiet}`}>{data?.aurora?.alert_state}</span>
                <p className="font-body text-[#737570] text-sm mt-3 text-center">{data?.aurora?.visibility}</p>
              </div>
            </Panel>

            {/* Weather */}
            <Panel icon={Cloud} title="Weather" id="weather" delay={0.3}>
              {data?.weather?.temperature_c!=null?(
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div><p className="font-display text-[#E4E4E0] text-4xl font-bold">{Math.round(data.weather.temperature_c)}<span className="text-lg text-[#737570]">&deg;C</span></p><p className="font-body text-[#737570] text-sm mt-0.5">{data.weather.condition}</p></div>
                    <div className="text-right space-y-1">
                      <div className="flex items-center gap-1.5 justify-end"><Thermometer className="w-3 h-3 text-[#737570]"/><span className="font-body text-[#E4E4E0] text-xs">Feels {Math.round(data.weather.feels_like_c)}&deg;C</span></div>
                      <div className="flex items-center gap-1.5 justify-end"><Droplets className="w-3 h-3 text-[#737570]"/><span className="font-body text-[#E4E4E0] text-xs">{data.weather.humidity}%</span></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#737570]/15">
                    <div><p className="font-body text-[#737570] text-[10px] uppercase tracking-widest mb-0.5">Cloud Cover</p><div className="flex items-center gap-2"><div className="flex-1 h-1.5 bg-[#1E2A1A] rounded-full overflow-hidden"><div className="h-full bg-[#737570] rounded-full" style={{width:`${data.weather.cloud_cover}%`}}/></div><span className="font-body text-[#E4E4E0] text-xs">{data.weather.cloud_cover}%</span></div></div>
                    <div><p className="font-body text-[#737570] text-[10px] uppercase tracking-widest mb-0.5">Water Temp</p><p className="font-display text-[#7A9E6E] text-lg font-semibold">{data.weather.water_temp_c!=null?`${Math.round(data.weather.water_temp_c)}\u00B0C`:"--"}</p></div>
                    <div className="flex flex-col items-center">
                      <p className="font-body text-[#737570] text-[10px] uppercase tracking-widest mb-1">Wind</p>
                      <svg viewBox="0 0 60 60" className="w-44 h-44">
                        <circle cx="30" cy="30" r="27" fill="none" stroke="#737570" strokeWidth="0.5" opacity="0.3"/>
                        <circle cx="30" cy="30" r="18" fill="none" stroke="#737570" strokeWidth="0.3" opacity="0.15" strokeDasharray="1.5 2.5"/>
                        <text x="30" y="6" textAnchor="middle" fill="#B0B1AD" fontSize="5" fontFamily="DM Sans" fontWeight="600">N</text>
                        <text x="30" y="58" textAnchor="middle" fill="#737570" fontSize="4" fontFamily="DM Sans">S</text>
                        <text x="4" y="32" textAnchor="middle" fill="#737570" fontSize="4" fontFamily="DM Sans">W</text>
                        <text x="56" y="32" textAnchor="middle" fill="#737570" fontSize="4" fontFamily="DM Sans">E</text>
                        <g transform={`rotate(${data.weather.wind_direction || 0} 30 30)`}>
                          <polygon points="30,9 33,22 30,20 27,22" fill="#7A9E6E"/>
                          <line x1="30" y1="20" x2="30" y2="45" stroke="#737570" strokeWidth="1.2" strokeLinecap="round"/>
                        </g>
                        <circle cx="30" cy="30" r="2.5" fill="#1E2A1A" stroke="#737570" strokeWidth="0.5"/>
                      </svg>
                      <span className="font-body text-[#E4E4E0] text-xs mt-0.5">{Math.round(data.weather.wind_speed_kmh)} km/h</span>
                    </div>
                  </div>
                </div>
              ):(<p className="font-body text-[#737570] text-sm">Weather data unavailable</p>)}
            </Panel>

            {/* Golden Hour & Dark Sky */}
            <Panel icon={Sun} title="Golden Hour & Dark Sky" id="golden-hour" delay={0.35}>
              <div className="space-y-4">
                <TimelineBar sun={data?.sun}/>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {[["Sunrise",data?.sun?.sunrise],["Sunset",data?.sun?.sunset],["Golden Hour",data?.sun?.golden_hour_start],["Civil Dusk",data?.sun?.civil_twilight_end]].map(([l,v])=>(
                    <div key={l} className="flex justify-between"><span className="font-body text-[#737570] text-xs">{l}</span><span className="font-body text-[#E4E4E0] text-xs font-medium">{v||"--"}</span></div>
                  ))}
                </div>
                {data?.sun?.dark_sky_start&&data?.sun?.dark_sky_end&&(
                  <div className="pt-3 border-t border-[#737570]/15">
                    <p className="font-body text-[#737570] text-[10px] uppercase tracking-widest mb-1">Dark Sky Window</p>
                    <p className="font-display text-[#E4E4E0] text-lg">{data.sun.dark_sky_start} — {data.sun.dark_sky_end}</p>
                  </div>
                )}
              </div>
            </Panel>

            {/* Fire & Safety */}
            <Panel icon={Flame} title="Fire & Safety" id="fire" delay={0.4}>
              {data?.fire?(
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${data.fire.level==="danger"?"bg-red-500/20":data.fire.level==="warning"?"bg-orange-500/20":data.fire.level==="caution"?"bg-yellow-500/20":"bg-[#7A9E6E]/20"}`}>
                      <Flame className={`w-5 h-5 ${data.fire.level==="danger"?"text-red-400":data.fire.level==="warning"?"text-orange-400":data.fire.level==="caution"?"text-yellow-400":"text-[#7A9E6E]"}`}/>
                    </div>
                    <div><p className={`font-display text-lg font-semibold ${data.fire.level==="danger"?"text-red-400":data.fire.level==="warning"?"text-orange-400":data.fire.level==="caution"?"text-yellow-400":"text-[#7A9E6E]"}`}>{data.fire.status}</p><p className="font-body text-[#737570] text-sm">{data.fire.message}</p></div>
                  </div>
                  <a href={data.fire.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-body text-[#7A9E6E] text-xs hover:text-[#E4E4E0] transition-colors">
                    Check official BurnSafe status <ExternalLink className="w-3 h-3"/>
                  </a>
                </div>
              ):(<p className="font-body text-[#737570] text-sm">Fire status unavailable</p>)}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
